/**
 * Centralized Error Handling Utility
 * Provides consistent error logging and user notification
 */

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Error categories for better organization
export const ERROR_CATEGORIES = {
  API: 'api',
  AUTH: 'authentication',
  VALIDATION: 'validation',
  NETWORK: 'network',
  UI: 'ui',
  DATA: 'data'
};

/**
 * Main error handler class
 */
class ErrorHandler {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors in memory
  }

  /**
   * Log and handle errors with proper categorization
   * @param {Error|string} error - The error object or message
   * @param {Object} options - Error handling options
   */
  handleError(error, options = {}) {
    const {
      severity = ERROR_SEVERITY.MEDIUM,
      category = ERROR_CATEGORIES.API,
      context = '',
      showToUser = true,
      userMessage = null
    } = options;

    const errorLog = {
      timestamp: new Date().toISOString(),
      message: error?.message || error,
      stack: error?.stack,
      severity,
      category,
      context,
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Store error in memory (could be sent to logging service)
    this.errors.unshift(errorLog);
    if (this.errors.length > this.maxErrors) {
      this.errors.pop();
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 ${severity.toUpperCase()} Error - ${category}`);
      console.error('Message:', errorLog.message);
      if (context) console.log('Context:', context);
      if (error?.stack) console.error('Stack:', error.stack);
      console.groupEnd();
    }

    // Show user-friendly message
    if (showToUser) {
      this.showUserNotification(userMessage || this.getUserFriendlyMessage(category, error));
    }

    // TODO: In production, send to error tracking service (e.g., Sentry)
    if (process.env.NODE_ENV === 'production' && severity === ERROR_SEVERITY.CRITICAL) {
      this.sendToErrorTracking(errorLog);
    }
  }

  /**
   * Generate user-friendly error messages
   */
  getUserFriendlyMessage(category, error) {
    const messages = {
      [ERROR_CATEGORIES.API]: 'Unable to connect to the server. Please try again.',
      [ERROR_CATEGORIES.AUTH]: 'Authentication failed. Please log in again.',
      [ERROR_CATEGORIES.VALIDATION]: 'Please check your input and try again.',
      [ERROR_CATEGORIES.NETWORK]: 'Network error. Please check your connection.',
      [ERROR_CATEGORIES.UI]: 'Something went wrong. Please refresh the page.',
      [ERROR_CATEGORIES.DATA]: 'Data processing error. Please try again.'
    };

    return messages[category] || 'An unexpected error occurred. Please try again.';
  }

  /**
   * Show user notification (simple alert for now, could be replaced with toast)
   */
  showUserNotification(message) {
    // Simple alert for now - could be replaced with a proper toast notification
    alert(message);
  }

  /**
   * Send error to tracking service (placeholder)
   */
  sendToErrorTracking(errorLog) {
    // TODO: Implement actual error tracking service integration
    console.log('Would send to error tracking:', errorLog);
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(count = 10) {
    return this.errors.slice(0, count);
  }

  /**
   * Clear error log
   */
  clearErrors() {
    this.errors = [];
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

/**
 * Convenience functions for common error scenarios
 */

export const handleApiError = (error, context = '') => {
  errorHandler.handleError(error, {
    severity: ERROR_SEVERITY.MEDIUM,
    category: ERROR_CATEGORIES.API,
    context: `API Error: ${context}`,
    userMessage: 'Unable to save your data. Please try again.'
  });
};

export const handleAuthError = (error, context = '') => {
  errorHandler.handleError(error, {
    severity: ERROR_SEVERITY.HIGH,
    category: ERROR_CATEGORIES.AUTH,
    context: `Auth Error: ${context}`,
    userMessage: 'Session expired. Please log in again.'
  });
};

export const handleValidationError = (error, context = '') => {
  errorHandler.handleError(error, {
    severity: ERROR_SEVERITY.LOW,
    category: ERROR_CATEGORIES.VALIDATION,
    context: `Validation Error: ${context}`,
    userMessage: 'Please check your input and try again.'
  });
};

export const handleNetworkError = (error, context = '') => {
  errorHandler.handleError(error, {
    severity: ERROR_SEVERITY.HIGH,
    category: ERROR_CATEGORIES.NETWORK,
    context: `Network Error: ${context}`,
    userMessage: 'Connection problem. Please check your internet and try again.'
  });
};

export const handleDataError = (error, context = '') => {
  errorHandler.handleError(error, {
    severity: ERROR_SEVERITY.MEDIUM,
    category: ERROR_CATEGORIES.DATA,
    context: `Data Error: ${context}`,
    userMessage: 'Data processing failed. Please try again.'
  });
};

// Generic error handler
export const handleError = (error, options = {}) => {
  errorHandler.handleError(error, options);
};

// Export the error handler instance for advanced usage
export { errorHandler };

export default errorHandler;